// Updated App.js with complete PayPal handling
import React, { useState, useEffect, useRef, Fragment } from "react";
import { useForm } from "react-hook-form";
import FormStep from "./components/FormStep";
import SimpleHidden from "./components/fields/SimpleHidden";
import FormProgress from "./components/FormProgress";
import Modal from "./components/Modal";
import MonthlyUpsellModal from "./components/MonthlyUpsellModal";
import { ContinueButton } from "./components/ContinueButton";
import {
	GoogleReCaptchaProvider,
	useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import ErrorBoundary from "./components/ErrorBoundary";
import Spinner from "./components/Spinner";
import cn from "classnames";
import {
	trackDonateStep,
	trackPurchase,
	trackMonthlyUpsellView,
	trackMonthlyUpsellAccept,
	trackMonthlyUpsellDecline,
	trackMonthlyUpsellChoice,
} from "./utils/tracking";
import {
	calculateSuggestedMonthlyAmount,
	isMonthlyUpsellDismissed,
	isSingleAmountEligibleForMonthlyUpsell,
	setMonthlyUpsellChoice,
} from "./utils/monthlyUpsell";

/**
 * Merge hidden DOM values into submit payload without clobbering RHF-held files.
 * image_upload must not use a DOM input for File values — coerced "[object File]"
 * strings must never overwrite real Files either.
 */
function mergeHiddenInputsFromDom(formData) {
	const formEl = document.querySelector(".strategiq-react-form");

	const carriesFiles = (val) =>
		val instanceof File ||
		(typeof FileList !== "undefined" && val instanceof FileList) ||
		(Array.isArray(val) && val.some((item) => item instanceof File));

	const isCoercedObjectString = (val) =>
		val === "[object File]" || val === "[object Object]";

	formEl?.querySelectorAll('input[type="hidden"]').forEach((el) => {
		const name = el.getAttribute("name");
		if (!name || el.value === undefined) return;
		if (carriesFiles(formData[name])) return;
		if (isCoercedObjectString(el.value)) return;
		formData[name] = el.value;
	});
}

// Wrap the App component with the GoogleReCaptchaProvider
const AppWithRecaptcha = (props) => (
	<GoogleReCaptchaProvider reCaptchaKey={props.config?.recaptcha_site_key}>
		<ErrorBoundary>
			<App {...props} />
		</ErrorBoundary>
	</GoogleReCaptchaProvider>
);

const App = ({ config }) => {
	const [currentStep, setCurrentStep] = useState(0);
	const [stepError, setStepError] = useState("");
	const [paymentMethods, setPaymentMethods] = useState(null);
	const [paymentMethodSuccess, setPaymentMethodSuccess] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isMonthlyUpsellOpen, setIsMonthlyUpsellOpen] = useState(false);
	const [suggestedMonthlyAmount, setSuggestedMonthlyAmount] = useState(null);
	const [upsellSingleAmount, setUpsellSingleAmount] = useState(null);
	const upsellBypassRef = useRef(false);
	const [loading, setLoading] = useState(false);
	// Extract configuration from props
	const formConfig = config?.config;
	const nonce = config?.nonce;
	const donateUrl = config?.donate_url || "/donate";
	const steps = formConfig?.steps;

	// If no configuration found, don't render
	if (!formConfig || !steps) {
		console.error(`No configuration found for form`);
		return null;
	}

	// Check if current step should be rendered in modal
	const currentStepConfig = steps[currentStep];
	const shouldRenderInModal = currentStepConfig?.modal === true;

	// Auto-open modal when step is rendered
	useEffect(() => {
		if (shouldRenderInModal) {
			setIsModalOpen(true);
		} else {
			setIsModalOpen(false);
		}
	}, [shouldRenderInModal, currentStep]);

	// Handle modal close
	const handleModalClose = () => {
		setIsModalOpen(false);
		setCurrentStep(0);
	};

	const {
		register,
		unregister,
		handleSubmit,
		formState: { errors, isValid },
		watch,
		setValue,
		getValues,
		trigger,
	} = useForm({
		mode: "onChange",
		defaultValues: {},
	});

	// console.log(getValues());
	const selectedPaymentMethodValue = watch("paymentmethod");
	const selectedPaymentMethod = paymentMethods?.find(
		(method) => method.type === selectedPaymentMethodValue,
	);

	// Add this hook inside App
	const { executeRecaptcha } = useGoogleReCaptcha();
	const [recaptchaToken, setRecaptchaToken] = useState("");

	useEffect(() => {
		const populateFromData = () => {
			const data = config?.data;
			if (data) {
				Object.entries(data).forEach(([key, value]) => {
					setValue(key, value);
				});
			}
			// Also read UTM/package params directly from URL (ensures they're set for hidden fields)
			if (typeof window !== "undefined") {
				const urlParams = new URLSearchParams(window.location.search);
				["utm_campaign", "utm_source", "utm_medium", "package", "campaign"].forEach(
					(param) => {
						const val = urlParams.get(param);
						if (val) setValue(param, val);
					}
				);
			}
		};
		populateFromData();
	}, [setValue, config?.data]);

	// Remove URL params when form changes
	useEffect(() => {
		const subscription = watch((value, { name, type }) => {
			if (type === "change" && name) {
				const url = new URL(window.location);
				if (name.includes("donation_frequency_and_amount")) {
					url.searchParams.delete("donation_frequency");
					url.searchParams.delete("donation_amount");
					url.searchParams.delete("frequency");
					url.searchParams.delete("amount");
					window.history.replaceState({}, "", url);
				} else {
					url.searchParams.delete(name);
					window.history.replaceState({}, "", url);
				}
			}
		});
		return () => subscription.unsubscribe();
	}, [watch]);

	// Handle digital wallet payment method success
	//can we move this to digitalWallet pament component?
	useEffect(() => {
		if (selectedPaymentMethod?.pr) {
			const handlePaymentMethod = async (event) => {
				selectedPaymentMethod.success = true;
				setValue(
					"digital_wallet_type",
					selectedPaymentMethod.digital_wallet_type,
				);
				//try and set first name and last name from billing details if we dont have them already
				if (!watch("forename")) {
					setValue(
						"forename",
						event.paymentMethod.billing_details.name?.split(" ")[0],
					);
				}
				if (!watch("surname")) {
					setValue(
						"surname",
						event.paymentMethod.billing_details.name?.split(" ")[1],
					);
				}

				//general donation fields
				setValue(
					"billing_address_line_1",
					event.paymentMethod.billing_details.address.line1,
				);
				setValue(
					"billing_address_line_2",
					event.paymentMethod.billing_details.address.line2,
				);
				setValue(
					"billing_town",
					event.paymentMethod.billing_details.address.city,
				);
				setValue(
					"billing_county",
					event.paymentMethod.billing_details.address.state,
				);
				setValue(
					"billing_postcode",
					event.paymentMethod.billing_details.address.postal_code,
				);
				setValue(
					"billing_country",
					event.paymentMethod.billing_details.address.country,
				);
				setValue("billing_email", event.paymentMethod.billing_details.email);
				setValue("billing_phone", event.paymentMethod.billing_details.phone);

				//set stripe payment method id and other requirmeents fro processing
				setValue("payment_method_id", event.paymentMethod.id);
				setValue("card_details", {
					brand: event.paymentMethod.card.brand,
					last4: event.paymentMethod.card.last4,
					exp_month: event.paymentMethod.card.exp_month,
					exp_year: event.paymentMethod.card.exp_year,
				});
				setValue("card_valid", true);

				event.complete("success");
				handleNext();
			};

			// Add the event listener
			selectedPaymentMethod.pr.on("paymentmethod", handlePaymentMethod);

			// Cleanup function to remove the event listener
			return () => {
				if (selectedPaymentMethod?.pr) {
					selectedPaymentMethod.pr.off("paymentmethod", handlePaymentMethod);
				}
			};
		}
	}, [selectedPaymentMethod]);

	// Watch for PayPal payment success via method success flag
	useEffect(() => {
		if (
			selectedPaymentMethod?.type === "paypal" &&
			selectedPaymentMethod?.success
		) {
			setPaymentMethodSuccess(true);
			setValue(
				"paypal_invoice_number",
				selectedPaymentMethod.paypal_invoice_number,
				{ shouldDirty: true },
			);
			setValue(
				"paypal_transaction_id",
				selectedPaymentMethod.paypal_transaction_id,
				{ shouldDirty: true },
			);
			if (selectedPaymentMethod.customer_id) {
				setValue("paypal_customer_id", selectedPaymentMethod.customer_id, {
					shouldDirty: true,
				});
			}
			// Defer so RHF form values are readable in handleNext/getValues
			queueMicrotask(() => {
				handleNext();
			});
		}
	}, [paymentMethods]);





	//useeffect to add to main body class the step number via strategiq-react-form-step-<step_number>
	useEffect(() => {
		//remove strategiq-react-form-step- type classes
		document.body.classList.forEach((className) => {
			if (className.startsWith("strategiq-react-form-step-")) {
				document.body.classList.remove(className);
			}
		});
		document.body.classList.add(`strategiq-react-form-step-${currentStep}`);
	}, [currentStep]);

	const currentSection = steps[currentStep];

	// Get current step field names
	const getCurrentStepFields = () => {
		if (!currentSection || !currentSection.sections) return [];

		const allRegisteredFields = Object.keys(getValues());
		const currentStepFields = [];
		currentSection.sections.forEach((section) => {
			section.fields.forEach((field) => {
				const fieldSlug = field.slug;
				currentStepFields.push(fieldSlug);
				const concatenatedFields = allRegisteredFields.filter(
					(registeredField) =>
						registeredField.startsWith(fieldSlug + "_") &&
						registeredField !== fieldSlug,
				);
				currentStepFields.push(...concatenatedFields);
			});
		});

		return [...new Set(currentStepFields)];
	};

	const scrollTo = (element) => {
		if (!element) return;
		// If the current step is rendered in a modal, scroll the modal content
		if (shouldRenderInModal && isModalOpen) {
			const modalContent = document.getElementById("gated-content-modal");
			//parent is lity-content
			const lityContent = modalContent.parentElement;
			if (!lityContent) return;
			if (!modalContent) return;

			const elementRect = element.getBoundingClientRect();
			const containerRect = modalContent.getBoundingClientRect();

			// Position of the element inside the scrollable container
			const offsetPosition =
				elementRect.top - containerRect.top + modalContent.scrollTop - 50; // small padding from the top

			lityContent.scrollTo({
				top: offsetPosition,
				behavior: "smooth",
			});
			return;
		}

		// Otherwise, scroll the main window taking the header height into account
		const headerHeight = document.querySelector("header")?.offsetHeight || 0;
		const elementPosition = element.getBoundingClientRect().top;
		const offsetPosition =
			elementPosition + window.pageYOffset - headerHeight - 200;
		window.scrollTo({
			top: offsetPosition,
			behavior: "smooth",
		});
	};

	const redirectToDonatePage = (overrides = {}) => {
		const url = new URL(donateUrl, window.location.origin);

		const amount =
			overrides.amount ??
			getValues("donation_frequency_and_amount.amount");
		const frequency =
			overrides.frequency ??
			getValues("donation_frequency_and_amount.frequency");

		url.searchParams.set("amount", amount);
		url.searchParams.set("frequency", frequency);
		if (getValues("service")) {
			url.searchParams.set("service", getValues("service"));
		}
		if (getValues("tribute_id") || getValues("responses.tribute_id")) {
			url.searchParams.set(
				"tribute_id",
				getValues("tribute_id") || getValues("responses.tribute_id"),
			);
		}
		if (getValues("appeal_id")) {
			url.searchParams.set("appeal_id", getValues("appeal_id"));
		}
		window.location.href = url.toString();
	};

	const submitFormData = async (
		formData,
		endpoint,
		nonce,
		executeRecaptcha,
	) => {
		try {
			if (typeof executeRecaptcha !== "function") {
				throw new Error(
					"Security check is still loading. Please wait a moment and try again.",
				);
			}

			const token = await executeRecaptcha("form_submit");
			if (!token) {
				throw new Error(
					"Security check failed. Please refresh the page and try again.",
				);
			}
			setRecaptchaToken(token);

			// Add recaptcha token to form data
			const dataToSubmit = { ...formData, recaptcha_token: token };

			// Always use FormData for consistency
			const body = new FormData();

			Object.entries(dataToSubmit).forEach(([key, value]) => {
				const isDomFileList =
					typeof FileList !== "undefined" && value instanceof FileList;

				// File values coerced onto a text input become "[object File]" — never send those
				if (
					typeof value === "string" &&
					(value === "[object File]" || value === "[object Object]")
				) {
					console.warn(
						`Skipping corrupted file field "${key}" — please re-upload the image.`,
					);
					return;
				}

				if (value instanceof File) {
					if (value.size === 0) {
						console.warn(`Skipping empty file field "${key}"`);
						return;
					}
					body.append(key, value);
				} else if (isDomFileList && value.length > 0) {
					// FileList — appending as PHP-style `field[]` so $_FILES['field'] nests correctly (field[0] often does not)
					Array.from(value).forEach((file) => {
						if (file instanceof File && file.size > 0) {
							body.append(`${key}[]`, file);
						}
					});
				} else if (
					Array.isArray(value) &&
					value.length > 0 &&
					value[0] instanceof File
				) {
					// Multiple files — use field[] convention for PHP's $_FILES
					value.forEach((file) => {
						if (file instanceof File && file.size > 0) {
							body.append(`${key}[]`, file);
						}
					});
				} else if (
					typeof value === "object" &&
					value !== null &&
					!(value instanceof File)
				) {
					// Objects (but not Files) - stringify them
					body.append(key, JSON.stringify(value));
				} else if (value !== null && value !== undefined) {
					// Regular form fields - convert to string
					body.append(key, String(value));
				}
			});

			const imageFieldKeys = ["photo", "other_images", "tribute_image"];
			const lostImageField = imageFieldKeys.find((key) => {
				const raw = formData[key];
				return (
					typeof raw === "string" &&
					(raw === "[object File]" || raw === "[object Object]")
				);
			});
			if (lostImageField) {
				throw new Error(
					"Your image upload was lost before saving. Please re-upload the photo and try again.",
				);
			}

			const headers = {
				"X-WP-Nonce": nonce || "",
				// Don't set Content-Type for FormData - browser will set it with boundary
			};

			const response = await fetch(endpoint, {
				method: "POST",
				headers,
				body,
			});

			const result = await response.json();

			if (result.success === false) {
				throw new Error(
					result.message ||
						"Step validation failed. Please check your information and try again.",
				);
			}

			return result;
		} catch (error) {
			console.error("Step endpoint error:", error);
			throw error;
		}
	};

	//used to check if form has payment method step, if its a donate form it will do.
	const isDonationForm = (formConfig) => {
		for (const step of formConfig.steps || []) {
			for (const section of step.sections || []) {
				for (const field of section.fields || []) {
					if (field.slug === "paymentmethod") {
						return true;
					}
				}
			}
		}
		return false;
	};

	// Handle 3D Secure authentication
	const handle3DSecureAuthentication = async (
		clientSecret,
		paymentIntentId,
		donationIdFromPaymentStep,
	) => {
		const donationId =
			donationIdFromPaymentStep ??
			getValues("responses")?.donation_id ??
			null;

		try {
			setLoading(true);

			if (!donationId) {
				setStepError(
					"Donation session expired. Please refresh the page and try again.",
				);
				return;
			}

			// Load Stripe if not already loaded
			if (!window.stripe) {
				const { loadStripe } = await import("@stripe/stripe-js");
				window.stripe = await loadStripe(config?.stripe_public_key);
			}

			if (!window.stripe) {
				throw new Error("Stripe failed to load");
			}

			// Use Stripe to confirm the payment intent with 3D Secure
			const { error, paymentIntent } =
				await window.stripe.confirmCardPayment(clientSecret);

			// Always call the confirm endpoint regardless of Stripe result
			const confirmResponse = await fetch(
				"/wp-json/donation/v2/confirm-payment",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-WP-Nonce": nonce || "",
					},
					body: JSON.stringify({
						donation_id: donationId,
						payment_intent_id: paymentIntentId,
						stripe_error: error
							? {
									code: error.code,
									message: error.message,
									type: error.type,
								}
							: null,
						payment_intent_status: paymentIntent?.status || null,
					}),
				},
			);

			const confirmResult = await confirmResponse.json();

			if (confirmResult.success && confirmResult.requires_action) {
				await handle3DSecureAuthentication(
					confirmResult.client_secret,
					confirmResult.payment_intent_id || paymentIntentId,
					donationId,
				);
				return;
			}

			if (confirmResult.success) {
				// Update form with confirmed payment details
				setValue("responses", {
					...getValues("responses"),
					...confirmResult,
					donation_id: donationId,
					payment_confirmed: true,
				});

				// Call donate_handler again to ensure normal donation processing runs
				let formData = getValues();
				mergeHiddenInputsFromDom(formData);
				const paymentStep = steps[currentStep];

				//set 3d secure authenticated flag to true which is used in donate handelr to properly complete the donation
				formData["3d_secure_authenticated"] = true;
				formData.responses = {
					...(formData.responses || {}),
					donation_id: donationId,
				};

				const donateResult = await submitFormData(
					formData,
					paymentStep.endpoint,
					nonce,
					executeRecaptcha,
				);

				// Update responses with final donation result
				setValue("responses", {
					...getValues("responses"),
					...donateResult,
				});

				// Fire tracking after successful payment
				if (isDonationForm(formConfig)) {
					try {
						trackPurchase(getValues());
					} catch (e) {
						console.error(e);
					}
				}

				// Proceed to next step
				setCurrentStep((prev) => prev + 1);
				scrollTo(document.querySelector(".form-step__header"));
			} else {
				setStepError(
					confirmResult.message ||
						"Payment confirmation failed. Please try again.",
				);
			}
		} catch (error) {
			console.error("3D Secure authentication error:", error);
			setStepError("Payment authentication failed. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const MONTHLY_UPSELL_CLASSNAMES = [
		"bar-donation-form",
		"donation-pod-form",
		"donation-form",
	];

	const isMonthlyUpsellForm = () => {
		if (formConfig?.exclude_monthly_upsell) return false;
		return MONTHLY_UPSELL_CLASSNAMES.includes(formConfig?.classname);
	};

	const monthlyUpsellConfig = formConfig?.monthly_upsell;

	const getMonthlyUpsellCookieOptions = () => ({
		path: monthlyUpsellConfig?.cookie_path || "/",
		domain: monthlyUpsellConfig?.cookie_domain || undefined,
	});

	const shouldOfferMonthlyUpsell = () => {
		if (!monthlyUpsellConfig?.enabled) return false;
		if (!isMonthlyUpsellForm()) return false;
		if (currentStep !== 0) return false;
		if (upsellBypassRef.current) {
			return false;
		}
		if (isMonthlyUpsellDismissed(monthlyUpsellConfig)) {
			return false;
		}
		const singleAmount = Number(
			getValues("donation_frequency_and_amount.amount"),
		);
		if (
			!isSingleAmountEligibleForMonthlyUpsell(
				singleAmount,
				monthlyUpsellConfig?.max_single_amount,
			)
		) {
			return false;
		}
		return (
			getValues("donation_frequency_and_amount.frequency") === "single"
		);
	};

	const handleMonthlyUpsellSwitch = () => {
		const singleAmount = Number(
			getValues("donation_frequency_and_amount.amount"),
		);
		const monthlyAmount = calculateSuggestedMonthlyAmount(singleAmount);

		setValue("donation_frequency_and_amount[frequency]", "monthly", {
			shouldValidate: true,
		});
		setValue("donation_frequency_and_amount[amount]", monthlyAmount, {
			shouldValidate: true,
		});

		setMonthlyUpsellChoice(
			"accepted",
			getMonthlyUpsellCookieOptions(),
		);

		try {
			trackMonthlyUpsellAccept(getValues(), monthlyAmount);
			trackMonthlyUpsellChoice("accepted", getValues(), monthlyAmount);
		} catch (err) {
			console.error(err);
		}

		setIsMonthlyUpsellOpen(false);

		if (formConfig?.classname === "donation-form") {
			upsellBypassRef.current = true;
			handleNext();
			return;
		}

		redirectToDonatePage({ frequency: "monthly", amount: monthlyAmount });
	};

	const handleMonthlyUpsellDecline = () => {
		setMonthlyUpsellChoice(
			"declined",
			getMonthlyUpsellCookieOptions(),
		);

		try {
			trackMonthlyUpsellDecline(getValues(), suggestedMonthlyAmount);
			trackMonthlyUpsellChoice(
				"declined",
				getValues(),
				suggestedMonthlyAmount,
			);
		} catch (err) {
			console.error(err);
		}

		upsellBypassRef.current = true;
		setIsMonthlyUpsellOpen(false);
		handleNext();
	};

	const handleMonthlyUpsellClose = () => {
		setIsMonthlyUpsellOpen(false);
	};

	// Updated handleNext function with cleaner form submission
	const handleNext = async (e) => {
		setStepError("");
		const currentStepFields = getCurrentStepFields();

		if (currentStepFields.length > 0) {
			const isValid = await trigger(currentStepFields);

			if (!isValid) {
				const firstError = document.querySelector(".field__error");
				if (firstError) {
					scrollTo(firstError);
				}
				return;
			}
		}

		if (shouldOfferMonthlyUpsell()) {
			const singleAmount = Number(
				getValues("donation_frequency_and_amount.amount"),
			);
			const monthlyAmount = calculateSuggestedMonthlyAmount(singleAmount);
			setSuggestedMonthlyAmount(monthlyAmount);
			setUpsellSingleAmount(singleAmount);
			setIsMonthlyUpsellOpen(true);
			try {
				trackMonthlyUpsellView(getValues(), monthlyAmount);
			} catch (err) {
				console.error(err);
			}
			return;
		}

		upsellBypassRef.current = false;

		// Handle payment method triggers if not already successful

		//if payment method is empty but we are on the payment step, redirect to donate page
		if (currentStepFields.includes("paymentmethod") && !selectedPaymentMethod) {
			redirectToDonatePage();
			return;
		}
		if (!selectedPaymentMethod?.success) {
			switch (selectedPaymentMethod?.triggerType) {
				case "digital_wallet":
					// Skip payment request window for simulated wallets in dev mode
					const isDev =
						typeof window !== "undefined" &&
						(window.location.href.includes("localhost") ||
							window.location.href.includes(".build"));
					const isSimulated =
						isDev &&
						selectedPaymentMethod.digital_wallet_type === "googlepay" &&
						selectedPaymentMethod.isSimulated;

					if (isSimulated) {
						// Simulate successful payment in dev mode
						selectedPaymentMethod.success = true;
						setValue(
							"digital_wallet_type",
							selectedPaymentMethod.digital_wallet_type,
						);
						// Continue with normal flow (will proceed to next step)
					} else {
						selectedPaymentMethod.pr.show();
						return;
					}
					break;

				case "button":
					redirectToDonatePage();
					return;
				case "paypal":
					return;
				default:
					break;
			}
		}

		// Check if current step has an endpoint to call
		if (currentSection.endpoint && currentSection.endpoint.trim() !== "") {
			try {
				setLoading(true);
				let formData = getValues();
				mergeHiddenInputsFromDom(formData);

				// PayPal: ensure capture IDs from the payment method object are on the payload
				if (
					selectedPaymentMethod?.type === "paypal" &&
					selectedPaymentMethod?.success
				) {
					formData.paypal_invoice_number =
						formData.paypal_invoice_number ||
						selectedPaymentMethod.paypal_invoice_number;
					formData.paypal_transaction_id =
						formData.paypal_transaction_id ||
						selectedPaymentMethod.paypal_transaction_id;
					if (formData.paypal_invoice_number) {
						setValue(
							"paypal_invoice_number",
							formData.paypal_invoice_number,
						);
					}
					if (formData.paypal_transaction_id) {
						setValue(
							"paypal_transaction_id",
							formData.paypal_transaction_id,
						);
					}
				}

				const result = await submitFormData(
					formData,
					currentSection.endpoint,
					nonce,
					executeRecaptcha,
				);

				setValue("responses", { ...getValues("responses"), ...result });

				// Promote tribute_id to top-level so re-submit / donate redirect use the same ID
				if (result?.tribute_id) {
					setValue("tribute_id", result.tribute_id, {
						shouldDirty: true,
					});
				}

				// Tribute images are saved on any /donate call that includes files. Clear them after a successful
				// save so a later step (payment or 3D Secure retry) does not upload the same files again.
				const tributeImg = formData.tribute_image;
				const isTributeImgFileList =
					typeof FileList !== "undefined" &&
					tributeImg instanceof FileList &&
					tributeImg.length > 0;
				const hadTributeImageFiles =
					tributeImg &&
					(tributeImg instanceof File ||
						isTributeImgFileList ||
						(Array.isArray(tributeImg) &&
							tributeImg.some((f) => f instanceof File)));
				if (hadTributeImageFiles && result && result.success !== false) {
					setValue(
						"tribute_image",
						Array.isArray(tributeImg) ? [] : null,
					);
				}

				// Handle 3D Secure authentication if required
				if (result.requires_action) {
					if (!result.client_secret) {
						setStepError(
							"Additional card authentication is required but could not be started. Please try again or contact support.",
						);
						return;
					}
					await handle3DSecureAuthentication(
						result.client_secret,
						result.payment_intent_id,
						result.donation_id,
					);
					return; // Don't proceed to next step yet
				}

				// Paid methods must complete before thank-you (prevents orphan PayPal captures looking "done")
				const paymentMethod =
					formData.paymentmethod || getValues("paymentmethod");
				const requiresImmediatePayment =
					paymentMethod === "paypal" ||
					paymentMethod === "credit_debit" ||
					paymentMethod === "digital_wallet";
				if (
					requiresImmediatePayment &&
					result.payment_status !== "completed"
				) {
					setStepError(
						result.message ||
							(paymentMethod === "paypal"
								? "PayPal payment could not be confirmed. Please try again or choose another payment method. If you were charged, contact support with your PayPal receipt."
								: "Payment could not be confirmed. Please try again or contact support."),
					);
					return;
				}

				// Fire tracking after successful endpoint responses
				try {
					if (isDonationForm(formConfig)) {
						const hasPaymentSuccess = result?.payment_status === "completed";
						if (hasPaymentSuccess) {
							trackPurchase(getValues());
						} else {
							trackDonateStep(currentStep + 1, getValues());
						}
					}
				} catch (e) {
					console.error(e);
				}
				setCurrentStep((prev) => prev + 1);
				scrollTo(document.querySelector(".form-step__header"));
			} catch (error) {
				setStepError(
					error.message ||
						"Unable to validate this step. Please try again or contact support if the problem persists.",
				);
				return;
			} finally {
				setLoading(false);
			}
		} else {
			// Steps without endpoints (e.g., Donation step index 0)
			if (currentStep === 0) {
				try {
					if (isDonationForm(formConfig)) {
						trackDonateStep(1, getValues());
					}
				} catch (e) {
					console.error(e);
				}
			}
			setCurrentStep((prev) => prev + 1);
			scrollTo(document.querySelector(".form-step__header"));
		}
	};

	const handlePrevious = () => {
		setCurrentStep((prev) => prev - 1);
		setPaymentMethodSuccess(false);
		selectedPaymentMethod.success = false;
		selectedPaymentMethod.triggerType = null; //reset trigger type so we can trigger it on other steps
		setStepError("");
		scrollTo(document.querySelector(".form-step__header"));
	};
	// Render the complete form content
	const renderFormContent = () => {
		return (
			<div
				className={cn(formConfig.classname)}
				style={{ position: "relative" }}
			>
				{loading && (
					<div className="spinner-overlay">
						<Spinner />
					</div>
				)}
				<form className="strategiq-react-form">
					{/* Render hidden fields from all steps at top level so they stay mounted and values persist when user advances steps */}
					{formConfig.steps?.flatMap((step) =>
						step.sections?.flatMap((section) =>
							(section.fields || [])
								.filter((f) => f.type === "hidden")
								.map((field) => (
									<SimpleHidden
										key={field.slug}
										register={register}
										id={field.slug}
										field={field}
									/>
								))
						)
					)}
					{formConfig.show_progress_bar && steps?.[currentStep]?.remove_from_progress !== true && (
						<FormProgress
							currentStep={currentStep}
							totalSteps={steps.length}
							setCurrentStep={setCurrentStep}
							config={config}
						/>
					)}

					<div className="form-section-wrapper">
						<FormStep
							step={steps[currentStep]}
							register={register}
							unregister={unregister}
							errors={errors}
							watch={watch}
							setValue={setValue}
							trigger={trigger}
							config={config}
							setPaymentMethods={setPaymentMethods}
							paymentMethods={paymentMethods}
							setCurrentStep={setCurrentStep}
						/>
					</div>

					<div className="form-navigation">
						{stepError && (
							<div className="step-error">
								<p className="field__error">{stepError}</p>
							</div>
						)}

						{currentStep > 0 && currentStep < steps.length - 1 && (
							<button
								type="button"
								className="previous-button"
								onClick={handlePrevious}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="20"
									height="20"
									viewBox="0 0 20 20"
									fill="none"
								>
									<rect
										x="20"
										width="20"
										height="20"
										transform="rotate(90 20 0)"
										fill="white"
										fillOpacity="0.01"
									/>
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M14.8108 18.0648L6.74626 10L14.8108 1.93524C15.0631 1.68304 15.0631 1.27461 14.8108 1.02241C14.5586 0.7702 14.1502 0.7702 13.898 1.02241L5.37701 9.54359C5.25091 9.66951 5.18785 9.83476 5.18785 10C5.18785 10.1651 5.25091 10.3303 5.37701 10.4564L13.898 18.9776C14.1502 19.2298 14.5586 19.2298 14.8108 18.9776C15.0631 18.7254 15.0631 18.317 14.8108 18.0648Z"
										fill="#00288C"
									/>
								</svg>
								{formConfig.steps[currentStep].previous_button_text ||
									formConfig.previous_button_text ||
									"Previous step"}
							</button>
						)}
						{steps &&
							currentStep !== steps.length - 1 &&
							!formConfig.steps[currentStep].hide_continue_button && (
								<ContinueButton
									paymentMethods={paymentMethods}
									selectedPaymentMethod={selectedPaymentMethod}
									setPaymentMethods={setPaymentMethods}
									continueButtonText={
										steps[currentStep].continue_button_text ||
										formConfig.continue_button_text ||
										"Continue"
									}
									currentStep={currentStep}
									steps={steps}
									config={config}
									onClick={handleNext}
								/>
							)}
						{currentStep === steps.length - 1 && shouldRenderInModal && (
							<button
								type="button"
								className="finish-button btn btn--solid btn--lime"
								onClick={() => {
									window.location.reload();
								}}
							>
								{steps[currentStep].finish_button_text ||
									formConfig.finish_button_text ||
									"Finish"}
							</button>
						)}
					</div>
				</form>
			</div>
		);
	};
	const monthlyUpsellModal = (
		<MonthlyUpsellModal
			isOpen={isMonthlyUpsellOpen}
			onClose={handleMonthlyUpsellClose}
			config={monthlyUpsellConfig}
			suggestedMonthlyAmount={suggestedMonthlyAmount}
			singleAmount={upsellSingleAmount}
			onSwitchToMonthly={handleMonthlyUpsellSwitch}
			onContinueOneOff={handleMonthlyUpsellDecline}
		/>
	);

	// If current step should be rendered in modal, show it in a modal
	if (shouldRenderInModal) {
		return (
			<>
				<Modal
					isOpen={isModalOpen}
					onClose={handleModalClose}
					title={currentStepConfig?.title}
					allowClose={true}
				>
					{renderFormContent()}
				</Modal>
				{monthlyUpsellModal}
			</>
		);
	}

	// Otherwise render normally
	return (
		<>
			{renderFormContent()}
			{monthlyUpsellModal}
		</>
	);
};

export default AppWithRecaptcha;
